import { C, mono } from "../../../lib/styles.js";
import { conflictSentence, describeHoldDates, equipmentLabel, holdTitle } from "../../../lib/holds.js";

/* Calendar chip primitives shared by the Dashboard strip and the Schedule month
   view (docs/holds/holds-plan.md, Frames B, C, E). Jobs are solid, holds are
   dashed, a conflict is an amber "!" on the chip's corner. Colours are tokens
   from styles.js only. A hold names its client and subject for every role, the
   way a booking does (Ivin, 2026-09-05); money never appears here. */

export const HOLD_COLOR = C.blue;

// The job chip exactly as the strip has always drawn it.
export const jobChipStyle = (clr, faded) => ({
  fontSize: 8, fontWeight: 500, fontFamily: mono,
  padding: "1px 5px", borderRadius: 3, lineHeight: "11px",
  background: `${clr}26`, color: clr, whiteSpace: "nowrap",
  maxWidth: 58, overflow: "hidden", textOverflow: "ellipsis",
  opacity: faded ? 0.55 : 1, letterSpacing: "0.02em",
});

// Same footprint, dashed outline, no fill.
export const holdChipStyle = (faded) => ({
  ...jobChipStyle(HOLD_COLOR, faded),
  background: "transparent",
  border: `1px dashed ${HOLD_COLOR}a0`,
  padding: "0 4px",
});

export const bangStyle = {
  width: 10, height: 10, borderRadius: 5, background: C.amber, color: C.surface,
  fontSize: 7, fontWeight: 500, fontFamily: mono, lineHeight: 1,
  display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none",
};

// The text on a chip or a bar. A job reads its code ("atlpow"); a hold reads
// "Hold · Brooks Films", or its bare label ("Hold 4812") when the record
// carries no client name yet (written before 2026-09-05, not backfilled).
export const chipLabelFor = (entry) => {
  if (entry.kind !== "hold") return entry.label;
  const client = String(entry.clientName || "").trim();
  return client ? `Hold · ${client}` : String(entry.label || "");
};

// The hover text of a hold chip or bar: the booking-shaped title plus its
// date(s) ("Brooks Films — Rooftop drone survey (Hold) · Oct 7").
export const holdTooltip = (entry, dates = null) => `${holdTitle(entry)} · ${describeHoldDates(dates || [entry.date])}`;

// The aria label of a hold bar ("Brooks Films — Rooftop drone survey (Hold), Oct 26 to Oct 28").
export const holdAriaLabel = (entry, dates = null) => `${holdTitle(entry)}, ${describeHoldDates(dates || [entry.date])}`;

// A hold whose only work on a date is a flight day reserves crew only, so the
// chip fades the way setup and strike chips do.
export const isFlightOnly = (entry) => Array.isArray(entry?.kinds) && entry.kinds.length > 0 && entry.kinds.every((k) => k === "flight");

// Wraps a chip so the badge can sit on its corner without the chip's own
// overflow clipping it. Renders the bare chip when there is nothing to flag.
export function ChipWithBadge({ conflicted, children }) {
  if (!conflicted) return children;
  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      {children}
      <span data-testid="cal-conflict-badge" title="Conflict" style={{ ...bangStyle, position: "absolute", top: -5, right: -6 }}>!</span>
    </div>
  );
}

export function HoldChip({ entry, conflicted, title }) {
  return (
    <ChipWithBadge conflicted={conflicted}>
      <div data-testid="cal-chip-hold" data-key={entry.key} title={title} style={holdChipStyle(isFlightOnly(entry))}>{chipLabelFor(entry)}</div>
    </ChipWithBadge>
  );
}

// Legend swatches. The multi-day and follow-up swatches stay inline on the
// Dashboard where they already live; these two are the additions.
const legendText = { fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em" };
export function LegendHold() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
      <div style={{ width: 18, height: 6, borderRadius: 2, background: "transparent", border: `1px dashed ${HOLD_COLOR}a0` }} />
      <span style={legendText}>Hold</span>
    </div>
  );
}
export function LegendConflict() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
      <span style={bangStyle}>!</span>
      <span style={legendText}>Conflict</span>
    </div>
  );
}
export function LegendJob() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
      <div style={{ width: 18, height: 6, borderRadius: 2, background: `${C.blue}40`, border: `1px solid ${C.blue}80` }} />
      <span style={legendText}>Job</span>
    </div>
  );
}

// The one line a hold prints on a day panel, the same for every role:
// "Hold 4812 · Client · Equipment · Crew" (the client is omitted only when the
// record carries none).
export function holdLine(entry, { equipmentMap = null } = {}) {
  const parts = [entry.label];
  if (entry.clientName) parts.push(entry.clientName);
  const eq = (entry.equipmentIds || []).map((id) => equipmentLabel(id, equipmentMap)).filter(Boolean);
  if (eq.length) parts.push(eq.join(", "));
  const crew = (entry.crewNames || []).filter(Boolean);
  if (crew.length) parts.push(crew.join(", "));
  return parts.join(" · ");
}

// Every conflict sentence for one entry, deduped, from a scheduleEntries()
// result. The sentences name the other hold's or job's client for every role;
// `anonymized` is kept for compatibility and no surface passes it.
export function conflictLinesFor(entry, schedule, { anonymized = false, equipmentMap = null } = {}) {
  const rec = schedule?.conflicts?.get(entry.key);
  if (!rec) return [];
  const out = [];
  const seen = new Set();
  rec.reasons.forEach((r) => {
    const s = conflictSentence(r, schedule.byKey, { anonymized, equipmentMap });
    if (s && !seen.has(s)) { seen.add(s); out.push(s); }
  });
  return out;
}

// "hold:<quoteId>|<date>" for every conflicted row, so a chip can be flagged by
// its owner and date without knowing the session id the key carries.
export function conflictedOwnerDates(schedule) {
  const set = new Set();
  if (!schedule?.conflicts) return set;
  for (const rec of schedule.conflicts.values()) {
    const owner = schedule.byKey.get(rec.key)?.owner;
    if (owner) set.add(`${owner}|${rec.date}`);
  }
  return set;
}
